/**
 * Steg 3 i sangimporten: kontroller før import.
 * Ren visning: valg og handlinger sendes opp til veiviseren. All tekst vises som tekst, aldri HTML.
 */
import React, { useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { TONEARTER } from './toneartliste';

export interface MusikkImportRad {
  radnr: number;
  status: 'ny' | 'finnes' | 'tekst';
  tittel: string;
  artist?: string | null;
  album?: string | null;
  utgivelsesaar?: number | string | null;
  lengde?: string | null;
  toneart?: string | null;
  bpm?: number | string | null;
  takt?: string | null;
  kategorier?: string | null;
  lenker?: string | null;
  tekst_og_akkorder?: string | null;
  notater?: string | null;
  publisert?: boolean | string | null;
}

interface Advarsel {
  radnr: number;
  kolonne?: string;
  melding: string;
}

/** Det brukeren kan rette på en rad. toneart satt (også tom) gir nedtrekk; uten toneart er den ren tekst. */
export interface RadRetting {
  artist: string;
  toneart?: string;
}

interface Steg3KontrollProps {
  rader: MusikkImportRad[];
  feilRadnr: ReadonlySet<number> | number[];
  advarsler: Advarsel[];
  valgte: ReadonlySet<number>;
  onVeksleRad: (radnr: number) => void;
  onVeksleAlle: () => void;
  onTilbake: () => void;
  onAvbryt: () => void;
  onImporter: () => void;
  opptatt?: boolean;
  /** Feil som hindrer all import (for eksempel filfeil fra serveren); knappen er deaktivert. */
  stoppet?: boolean;
  /** Serverens feilmeldinger per rad; vises på raden i stedet for en felles tekst. */
  feil?: Advarsel[];
  /** Overstyrer «Steg 3 av 3» (når steg 2 er hoppet over er dette «Steg 2 av 2»). */
  stegtekst?: string;
  /** Meldinger som ikke hører til en rad (filadvarsler, filfeil); vises over listen. */
  meldinger?: string[];
  /** Rader brukeren kan rette (sangfiler), per radnr. Andre rader vises som ren tekst. */
  rettbare?: Record<number, RadRetting>;
  onArtistEndret?: (radnr: number, artist: string) => void;
  onToneartEndret?: (radnr: number, toneart: string) => void;
  /** Artistfeltene er åpne også mens `opptatt` er satt: veiviseren holder rettelsene i kø. */
  artistRedigerbar?: boolean;
  /** Ny forhåndsvisning pågår etter en rettelse: lasteindikator over listen. */
  oppdaterer?: boolean;
  /** Elementet stegtittelen vises i; veiviseren sender dialogens DialogTitle. */
  Tittel?: React.ElementType;
}

const sang = (n: number, entall: string, flertall: string) => `${n} ${n === 1 ? entall : flertall}`;

const oppsummering = (rader: MusikkImportRad[], feil: Set<number>): string => {
  const gyldige = rader.filter((r) => !feil.has(r.radnr));
  const ny = gyldige.filter((r) => r.status === 'ny').length;
  const tekst = gyldige.filter((r) => r.status === 'tekst').length;
  const finnes = gyldige.filter((r) => r.status === 'finnes').length;
  return [
    ny > 0 && sang(ny, 'ny sang', 'nye sanger'),
    tekst > 0 && `${tekst} får tekst`,
    finnes > 0 && `${finnes} finnes fra før`,
  ]
    .filter(Boolean)
    .join(' · ');
};

const roligLinje = (r: MusikkImportRad): string =>
  [r.utgivelsesaar, r.kategorier, r.lenker]
    .filter((v) => v !== null && v !== undefined && String(v).trim() !== '')
    .join(' · ');

/** Feltet beholder fokus ved trykk på knappen: blur her ville startet en forhåndsvisning og deaktivert knappen før klikket. */
const beholdFokus = (e: React.MouseEvent) => e.preventDefault();

/** Artist som tekstfelt. Endringen sendes opp når feltet forlates eller Enter trykkes, ikke for hvert tastetrykk. */
const ArtistFelt: React.FC<{ tittel: string; verdi: string; deaktivert: boolean; ugyldig: boolean; onEndret: (artist: string) => void }> = ({
  tittel,
  verdi,
  deaktivert,
  ugyldig,
  onEndret,
}) => {
  const [utkast, setUtkast] = useState(verdi);
  const sist = useRef(verdi);
  const [forrigeVerdi, setForrigeVerdi] = useState(verdi);
  if (verdi !== forrigeVerdi) {
    // Verdien ble endret utenfra (ny forhåndsvisning): utkastet følger den.
    setForrigeVerdi(verdi);
    setUtkast(verdi);
    sist.current = verdi;
  }
  const send = () => {
    const ny = utkast.trim();
    if (ny === sist.current.trim()) return;
    sist.current = ny;
    onEndret(ny);
  };
  return (
    <Input
      aria-label={`Artist for ${tittel}`}
      aria-invalid={ugyldig || undefined}
      placeholder="Artist"
      className="h-8 text-sm"
      value={utkast}
      disabled={deaktivert}
      onChange={(e) => setUtkast(e.target.value)}
      onBlur={send}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          send();
        }
      }}
    />
  );
};

const Steg3Kontroll: React.FC<Steg3KontrollProps> = ({
  rader,
  feilRadnr,
  advarsler,
  valgte,
  onVeksleRad,
  onVeksleAlle,
  onTilbake,
  onAvbryt,
  onImporter,
  opptatt = false,
  stoppet = false,
  feil: feilmeldinger = [],
  stegtekst = 'Steg 3 av 3',
  meldinger = [],
  rettbare = {},
  onArtistEndret,
  onToneartEndret,
  artistRedigerbar = false,
  oppdaterer = false,
  Tittel = 'h2',
}) => {
  const [apne, setApne] = useState<Set<number>>(new Set());
  const feil = new Set(feilRadnr);
  const velgbare = rader.filter((r) => !feil.has(r.radnr) && r.status !== 'finnes');
  const antallValgte = velgbare.filter((r) => valgte.has(r.radnr)).length;
  const alleStatus: boolean | 'indeterminate' =
    antallValgte === 0 ? false : antallValgte === velgbare.length ? true : 'indeterminate';

  const veksleTekst = (radnr: number) =>
    setApne((forrige) => {
      const neste = new Set(forrige);
      if (!neste.delete(radnr)) neste.add(radnr);
      return neste;
    });

  return (
    <div className="min-w-0 space-y-4">
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">{stegtekst}</p>
        <Tittel
          tabIndex={-1}
          data-steg-tittel=""
          className="text-lg font-semibold leading-none tracking-tight focus:outline-none"
        >
          Kontroller før import
        </Tittel>
        <p className="text-sm font-medium">{oppsummering(rader, feil)}</p>
      </div>

      {oppdaterer && (
        <p role="status" aria-label="Oppdaterer kontrollen" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Oppdaterer kontrollen
        </p>
      )}

      {meldinger.length > 0 && (
        <ul className="space-y-1">
          {meldinger.map((m, i) => (
            <li key={i} role="status" className="text-sm text-status-waiting">
              {m}
            </li>
          ))}
        </ul>
      )}

      {velgbare.length > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <Checkbox id="steg3-velg-alle" aria-label="Velg alle" checked={alleStatus} onCheckedChange={() => onVeksleAlle()} />
          <label htmlFor="steg3-velg-alle">Velg alle</label>
        </div>
      )}

      <ul data-testid="steg3-liste" className="max-h-80 min-w-0 divide-y overflow-y-auto rounded-md border">
        {rader.map((r) => {
          const erFeil = feil.has(r.radnr);
          const finnes = !erFeil && r.status === 'finnes';
          const radAdvarsler = advarsler.filter((a) => a.radnr === r.radnr);
          const retting = rettbare[r.radnr];
          const manglerArtist = !!retting && retting.artist.trim() === '';
          const radFeil = feilmeldinger.filter((a) => a.radnr === r.radnr && !(manglerArtist && a.kolonne === undefined && /^Artist:/.test(a.melding)));
          const linje = roligLinje(r);
          const erApen = apne.has(r.radnr);
          const harTekst = !!r.tekst_og_akkorder?.trim();
          return (
            <li key={r.radnr} className={cn('flex min-w-0 items-start gap-3 p-3', finnes && 'opacity-60')}>
              {!erFeil && (
                <Checkbox
                  className="mt-1"
                  aria-label={`Velg ${r.tittel}`}
                  checked={!finnes && valgte.has(r.radnr)}
                  disabled={finnes}
                  onCheckedChange={() => onVeksleRad(r.radnr)}
                />
              )}
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{r.tittel}</p>
                    {retting ? (
                      <ArtistFelt
                        tittel={r.tittel}
                        verdi={retting.artist}
                        deaktivert={opptatt && !artistRedigerbar}
                        ugyldig={manglerArtist}
                        onEndret={(artist) => onArtistEndret?.(r.radnr, artist)}
                      />
                    ) : (
                      r.artist && <p className="truncate text-sm text-muted-foreground">{r.artist}</p>
                    )}
                  </div>
                  {retting?.toneart !== undefined ? (
                    <select
                      aria-label={`Toneart for ${r.tittel}`}
                      value={retting.toneart}
                      disabled={opptatt}
                      onChange={(e) => onToneartEndret?.(r.radnr, e.target.value)}
                      className="h-8 shrink-0 rounded-md border border-input bg-background px-2 text-sm"
                    >
                      {retting.toneart === '' && <option value="">Velg toneart</option>}
                      {(retting.toneart === '' || TONEARTER.includes(retting.toneart) ? TONEARTER : [retting.toneart, ...TONEARTER]).map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  ) : (
                    r.toneart && <span className="shrink-0 text-sm">{r.toneart}</span>
                  )}
                </div>
                {erFeil &&
                  (manglerArtist ? [{ radnr: r.radnr, melding: 'Fyll inn artist' }, ...radFeil] : radFeil.length > 0 ? radFeil : [{ radnr: r.radnr, melding: 'Raden har feil og hoppes over' }]).map((f, i) => (
                    <p key={i} role="alert" className="text-sm text-destructive">
                      {f.melding}
                    </p>
                  ))}
                {radAdvarsler.map((a, i) => (
                  <p key={i} role="status" className="text-sm text-status-waiting">
                    {a.melding}
                  </p>
                ))}
                {finnes && <p className="text-sm text-muted-foreground">Finnes allerede, hoppes over</p>}
                {!erFeil && r.status === 'tekst' && (
                  <p className="text-sm text-muted-foreground">Får tekst, ellers ingen endring</p>
                )}
                {linje && <p className="break-words text-xs text-muted-foreground">{linje}</p>}
                {harTekst && (
                  <>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      aria-expanded={erApen}
                      onClick={() => veksleTekst(r.radnr)}
                    >
                      {erApen ? 'Skjul tekst' : 'Vis tekst'}
                    </Button>
                    {erApen && (
                      <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded bg-muted p-2 font-mono text-xs">
                        {r.tekst_og_akkorder}
                      </pre>
                    )}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="outline" disabled={opptatt} onMouseDown={beholdFokus} onClick={onTilbake}>
          Tilbake
        </Button>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" disabled={opptatt} onMouseDown={beholdFokus} onClick={onAvbryt}>
            Avbryt
          </Button>
          <Button
            type="button"
            disabled={opptatt || stoppet || antallValgte === 0}
            onMouseDown={beholdFokus}
            onClick={(e) => {
              // Fokus flyttes først nå, så en rettelse som ikke er sendt, er på vei før importen bes om.
              e.currentTarget.focus();
              onImporter();
            }}
          >
            {opptatt && !oppdaterer && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
            {antallValgte === 0 ? (velgbare.length === 0 ? 'Ingen nye sanger å importere' : 'Velg minst én sang') : `Importer ${sang(antallValgte, 'sang', 'sanger')}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Steg3Kontroll;
